import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Moon, Sun, Menu, X, ArrowUpRight, ShoppingCart } from "lucide-react";
import { LogoWord } from "./Brand";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Button } from "./ui/button";
import CartDrawer from "./CartDrawer";
import { scrollToId } from "../lib/scroll";

/** Primary commercial destinations — kept lean so the header breathes. */
const PRIMARY_LINKS = [
  { to: "/services", label: "Services" },
  { to: "/products", label: "Products" },
  { to: "/genesys", label: "GENESYS" },
  { to: "/shop", label: "Shop" },
];

/** Landing-page section anchors — available in the menu, not crowded into the desktop bar. */
const SECTION_NAV = [
  { href: "#meaning", label: "Company" },
  { href: "#divisions", label: "Services Overview" },
  { href: "#products", label: "Portfolio" },
  { href: "#epc", label: "EPC" },
  { href: "#why", label: "Why Us" },
  { href: "#research", label: "Research" },
  { href: "#contact", label: "Contact" },
];

export default function Header({ onOpenAI }) {
  const { theme, toggle } = useTheme();
  const { user, isAuthenticated } = useAuth();
  const { cart } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const isSub = location.pathname.startsWith("/portal") || location.pathname.startsWith("/admin")
    || location.pathname.startsWith("/products") || location.pathname.startsWith("/services")
    || location.pathname.startsWith("/genesys") || location.pathname.startsWith("/shop")
    || location.pathname.startsWith("/checkout") || ["/login", "/register"].includes(location.pathname);

  const isHome = location.pathname === "/";

  const goSection = (href) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (isHome) {
      scrollToId(href);
      window.history.replaceState(null, "", href);
      return;
    }
    navigate(`/${href}`);
  };

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, delay: 0.1, ease: [0.19, 1, 0.22, 1] }}
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-[90] transition-all duration-500 ${
        scrolled || isSub ? "backdrop-blur-xl bg-background/80 border-b border-border" : "bg-transparent"
      }`}
    >
      {(scrolled || isSub) && <span className="absolute bottom-0 inset-x-0 gold-rule" aria-hidden="true" />}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12 h-[72px] sm:h-[76px] flex items-center justify-between gap-3">
        <Link to="/" data-testid="header-logo-link" className="shrink-0"><LogoWord compact /></Link>

        <nav className="hidden lg:flex items-center gap-1 xl:gap-2" aria-label="Primary">
          {PRIMARY_LINKS.map((link) => {
            const active = location.pathname === link.to
              || (link.to !== "/" && location.pathname.startsWith(link.to));
            return (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-route-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`inline-flex items-center px-3 xl:px-3.5 py-2.5 font-mono-2 text-[11px] tracking-[0.18em] uppercase transition-colors duration-500 rounded-sm ${
                  active
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {!isSub && onOpenAI && (
            <button onClick={onOpenAI} data-testid="open-ai-finder-btn"
              className="hidden md:inline-flex items-center gap-2 px-3.5 py-2.5 text-[10px] uppercase tracking-[0.2em] font-mono-2 btn-outline">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--teal)] animate-pulse" />
              Engineering Assistant
            </button>
          )}

          <button onClick={toggle} data-testid="theme-toggle-btn" aria-label="Toggle theme"
            className="w-10 h-10 sm:w-11 sm:h-11 border border-border flex items-center justify-center hover:border-[color:var(--gold)] transition-colors">
            {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
          </button>

          <button onClick={() => setCartOpen(true)} data-testid="cart-icon-btn" aria-label="Open cart"
            className="relative w-10 h-10 sm:w-11 sm:h-11 border border-border flex items-center justify-center hover:border-[color:var(--gold)] transition-colors">
            <ShoppingCart size={15} />
            {cart?.item_count > 0 && (
              <span data-testid="cart-item-count-badge"
                className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-mono-2 rounded-full bg-[color:var(--gold)] text-[color:var(--ink)]">
                {cart.item_count}
              </span>
            )}
          </button>

          {isAuthenticated ? (
            <Link to={user.role === "admin" ? "/admin" : "/portal"} data-testid="header-portal-link">
              <Button size="sm" className="btn-gold rounded-none clip-frame-sm h-10 sm:h-11 px-3 sm:px-4">
                {user.role === "admin" ? "Admin" : "Portal"} <ArrowUpRight size={14} />
              </Button>
            </Link>
          ) : (
            <Link to="/login" data-testid="header-login-link" className="hidden sm:block">
              <Button size="sm" className="btn-gold rounded-none clip-frame-sm h-10 sm:h-11 px-4">Portal</Button>
            </Link>
          )}

          <button onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-btn" aria-label="Open menu"
            className="lg:hidden w-10 h-10 sm:w-11 sm:h-11 border border-border flex items-center justify-center">
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
            className="lg:hidden overflow-hidden border-t border-border bg-background/95 backdrop-blur-xl"
            data-testid="mobile-menu"
          >
            <div className="px-5 sm:px-6 py-5 flex flex-col gap-0.5 max-h-[min(80vh,640px)] overflow-y-auto">
              <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-2">Navigate</div>
              {PRIMARY_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  className="py-3.5 display text-2xl border-b border-border/60 leading-none"
                >
                  {link.label}
                </Link>
              ))}

              {isHome && (
                <>
                  <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mt-5 mb-2">On this page</div>
                  {SECTION_NAV.map((n) => (
                    <a
                      key={n.href}
                      href={n.href}
                      onClick={goSection(n.href)}
                      className="py-3.5 text-base border-b border-border/60 text-steel-text"
                    >
                      {n.label}
                    </a>
                  ))}
                </>
              )}

              {onOpenAI && !isSub && (
                <button
                  type="button"
                  onClick={() => { setOpen(false); onOpenAI(); }}
                  className="mt-4 py-3.5 text-left display text-xl text-[color:var(--teal)]"
                  data-testid="mobile-open-ai-finder-btn"
                >
                  Engineering Assistant
                </button>
              )}

              {!isAuthenticated && (
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="mt-2 py-3.5 display text-2xl text-[color:var(--gold)]"
                >
                  Client Portal
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
    </motion.header>
  );
}
